import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderMultiSectionDigitalClockTimeView } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import type { ProductPrice } from "@viajero/proto/guest/v1";
import type { PriceQuantities } from "@/lib/courseBookingContext";
import { type PartySizeRange, selectableMaxQuantity } from "@/lib/partySizeRange";
import { colors } from "@/theme/colors";

// 予約時刻は "HH:MM" 文字列で保持し、ピッカーには Dayjs で渡す。厳密解析 (第 3 引数 true) にして
// 空文字や不正な時刻を未選択扱いにする (既定の解析は "25:00" を 01:00 に丸めてしまう)。
dayjs.extend(customParseFormat);
const TIME_FORMAT = "HH:mm";

// 利用日は "YYYY-MM-DD" 文字列で保持し、時刻と同じく厳密解析で Dayjs に変換する。
const DATE_FORMAT = "YYYY-MM-DD";

// 予約時刻は毎時 00 分 / 30 分のみ受け付ける (JPTP_CANSEI-1500)。
const TIME_STEPS = { minutes: 30 } as const;

// スマートフォン等のタッチ端末では TimePicker の既定ビューがアナログ時計になり、timeSteps
// (デジタル表示の候補刻み) が効かず任意の分を選べてしまう。時・分ともデジタル表示のビューを
// 明示して、どの端末でも候補を 00 / 30 のみにする。
const TIME_VIEW_RENDERERS = {
  hours: renderMultiSectionDigitalClockTimeView,
  minutes: renderMultiSectionDigitalClockTimeView,
} as const;

// モーダル (タッチ端末) は幅が固定のため、既定の固定幅の列だと左端に寄って右側に大きな余白が残る。
// モーダルのときだけ列を等幅に広げ、各列の中で候補を中央に置く。
// (デスクトップのポップオーバーは内容幅なのでそのままで崩れない)
const TIME_VIEW_MOBILE_SX = {
  "& .MuiMultiSectionDigitalClock-root": { width: "100%" },
  "& .MuiMultiSectionDigitalClockSection-root": { flex: 1, maxWidth: "none", width: "auto" },
  "& .MuiMultiSectionDigitalClockSection-item": { marginInline: "auto" },
} as const;

// MUI X の DatePicker はロケールを ja にしても日付書式の語順が英語のままで、ツールバー
// (選択中の年月日) が "D MMMM" → 「6 9月」、カレンダーヘッダー (年月のプルダウン) が
// "MMMM YYYY" → 「9月 2026」と読みづらくなる (JPTP_CANSEI-1691)。日本語表示のときだけ
// 書式を明示して年月日の順に揃える。英語表示は MUI の既定書式のままでよい。
const JA_TOOLBAR_DATE_FORMAT = "YYYY年MM月DD日";
const JA_CALENDAR_HEADER_FORMAT = "YYYY年MM月";

type Props = {
  prices: ProductPrice[];
  date: string;
  minDate: string;
  time: string;
  // 店舗の回転設定 on のとき選択できる開始時間 ("HH:MM")。null は回転 off (自由入力)。
  turnStartTimes: string[] | null;
  quantities: PriceQuantities;
  // 商品と店舗の制限を合成した人数の実効範囲 (選択肢の上限に使う)。
  partySizeRange: PartySizeRange;
  totalPrice: number;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  onQuantityChange: (priceTypeId: string, quantity: number) => void;
};

/**
 * コース詳細画面の予約入力フォーム (利用日・時間・価格種別ごとの人数・合計金額)。
 * 純粋な表示コンポーネントとし、状態は呼び出し側 (CourseDetailView) が保持する。
 */
export function CourseBookingForm({
  prices,
  date,
  minDate,
  time,
  turnStartTimes,
  quantities,
  partySizeRange,
  totalPrice,
  onDateChange,
  onTimeChange,
  onQuantityChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.resolvedLanguage === "ja";
  // 価格種別 (大人 / 小人など) ごとに独立したセレクトのため、下限は合計で判定するしかない。
  // ここで絞るのは上限のみとし、下限未満は呼び出し側がメッセージとボタン非活性で示す。
  const maxQuantity = selectableMaxQuantity(partySizeRange);
  const dateValue = dayjs(date, DATE_FORMAT, true);
  const minDateValue = dayjs(minDate, DATE_FORMAT, true);
  const timeValue = dayjs(time, TIME_FORMAT, true);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: colors.textMuted, fontSize: 14, fontWeight: 600, mb: 2 }}>
          {t("courseDetail.dateTime")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* 利用日はネイティブ input (type="date") だと値の配置と操作アイコンがブラウザ依存に
              なり、iOS Safari は text-align を無視して中央寄せ、Android Chrome はカレンダー
              アイコンが下向き矢印になって右端に貼り付く (JPTP_CANSEI-1640)。時間と同じ
              MUI X のピッカーにして、どの端末でも右寄せ + カレンダーアイコンで揃える。
              値は input 要素ではなくセクション (span) の並びで描画されるため、右寄せは
              時間と同じくセクションコンテナ側で行う。 */}
          <DatePicker
            label={t("courseDetail.date")}
            value={dateValue.isValid() ? dateValue : null}
            onChange={(value) => onDateChange(value?.isValid() ? value.format(DATE_FORMAT) : "")}
            // 過去日を選べないようにする (ネイティブ input の min 属性の置き換え)。
            minDate={minDateValue.isValid() ? minDateValue : undefined}
            slotProps={{
              textField: {
                sx: { "& .MuiPickersSectionList-root": { justifyContent: "flex-end" } },
              },
              toolbar: isJapanese ? { toolbarFormat: JA_TOOLBAR_DATE_FORMAT } : undefined,
              calendarHeader: isJapanese ? { format: JA_CALENDAR_HEADER_FORMAT } : undefined,
            }}
            sx={{ minWidth: 180 }}
          />
          {turnStartTimes !== null ? (
            /* 回転設定 on の店舗は開始時間のみ予約できるため、自由入力の TimePicker ではなく
               開始時間だけを並べたセレクトにする (JPTP_CANSEI-1665)。開始時間に無い値
               (回転設定の変更前に選んだ時刻など) は未選択として表示する。 */
            <TextField
              select
              label={t("courseDetail.time")}
              value={turnStartTimes.includes(time) ? time : ""}
              onChange={(e) => onTimeChange(e.target.value)}
              sx={{ minWidth: 140, "& .MuiSelect-select": { textAlign: "right" } }}
            >
              {turnStartTimes.map((startTime) => (
                <MenuItem key={startTime} value={startTime}>
                  {startTime}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            /* 時刻は MUI X の TimePicker にして分候補を 30 分刻み (00 / 30) に絞る
               (JPTP_CANSEI-1500)。ネイティブ input の step 属性ではピッカーの分候補を絞れない。 */
            <TimePicker
              label={t("courseDetail.time")}
              value={timeValue.isValid() ? timeValue : null}
              onChange={(value) => onTimeChange(value?.isValid() ? value.format(TIME_FORMAT) : "")}
              timeSteps={TIME_STEPS}
              viewRenderers={TIME_VIEW_RENDERERS}
              ampm={false}
              // ピッカーのフィールドは書式を常に表示するためラベルは自動で shrink する。
              // 値は input 要素ではなくセクション (span) の並びで描画されるため、右寄せは
              // セクションコンテナ側で行う (利用日と表示位置を揃える)。
              slotProps={{
                textField: {
                  sx: { "& .MuiPickersSectionList-root": { justifyContent: "flex-end" } },
                },
                mobilePaper: { sx: TIME_VIEW_MOBILE_SX },
              }}
              sx={{ minWidth: 140 }}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: colors.textMuted, fontSize: 14, fontWeight: 600, mb: 2 }}>
          {t("courseDetail.quantitySelection")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {prices.map((price) => {
            const key = price.priceTypeId.toString();
            return (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ flex: 1, pl: 1 }}>
                  <Typography sx={{ color: colors.text, fontWeight: 600 }}>{price.name}</Typography>
                  <Typography sx={{ color: colors.textSub, fontSize: 13 }}>
                    ¥{price.price.toLocaleString()}
                  </Typography>
                </Box>
                <TextField
                  select
                  value={quantities[key] ?? 0}
                  onChange={(e) => onQuantityChange(key, Number(e.target.value))}
                  sx={{
                    minWidth: 88,
                    "& .MuiSelect-select": { textAlign: "right", pr: "40px !important" },
                  }}
                >
                  {Array.from({ length: maxQuantity + 1 }, (_, i) => i).map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          border: `1px solid ${colors.border}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography sx={{ color: colors.textMuted, fontSize: 14, fontWeight: 600 }}>
          {t("courseDetail.totalPrice")}
        </Typography>
        <Typography sx={{ color: colors.text, fontSize: 22, fontWeight: 700, textAlign: "right" }}>
          ¥{totalPrice.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
}
